(() => {
  'use strict';
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion:reduce)');
  const mobile = matchMedia('(max-width:760px),(max-width:1023px) and (pointer:coarse)');
  const clamp = value => Math.max(0, Math.min(100, value));

  function initialize(scope) {
    const hosts = scope.matches?.('[data-ura-piece]') ? [scope] : scope.querySelectorAll('[data-ura-piece]');
    hosts.forEach(host => {
      if (instances.has(host)) return;
      const product = host.closest('[data-ura4-product]');
      const gallery = host.closest('[data-ura4-gallery]');
      const stage = gallery?.querySelector('[data-ura4-gallery-stage]');
      const wrap = gallery?.querySelector('[data-ura4-gallery-stage-wrap]');
      const overlay = host.querySelector('[data-piece-detail]');
      const viewport = overlay?.querySelector('[data-piece-viewport]');
      const image = overlay?.querySelector('[data-piece-image]');
      const context = overlay?.querySelector('[data-piece-context]');
      const status = overlay?.querySelector('[data-piece-status]');
      const controls = [...host.querySelectorAll('[data-piece-action]')];
      const pieceButton = controls.find(button => button.dataset.pieceAction === 'piece');
      const detailButton = controls.find(button => button.dataset.pieceAction === 'detail');
      const fitButton = controls.find(button => button.dataset.pieceAction === 'fit');
      if (!product || !gallery || !stage || !wrap || !overlay || !viewport || !image || !context || !status || !pieceButton || !detailButton || !fitButton || !stage.querySelector('[data-ura4-slide] img')) return;

      const controller = new AbortController();
      const signal = controller.signal;
      let isDetail = false, source = '', request = 0, queuedFrame = 0, disposed = false;
      let x = 50, y = 50, drag = null, priorInert = false, priorHidden = null;
      const overlayHome = document.createComment('URA piece detail');
      overlay.before(overlayHome);
      wrap.append(overlay);

      const activePhoto = () => {
        const currentId = gallery.querySelector('[data-ura4-thumb][aria-current="true"]')?.dataset.imageId;
        const slides = [...stage.querySelectorAll('[data-ura4-slide]')];
        const slide = (currentId && slides.find(node => node.dataset.imageId === currentId)) || slides.find(node => node.classList.contains('is-active')) || slides[0];
        const frame = slide?.querySelector('[data-ura4-image-frame]');
        const photo = frame?.querySelector('img') || slide?.querySelector('img');
        return { url: frame?.dataset.fullSrc || photo?.currentSrc || photo?.src || '', alt: photo?.alt || slide?.dataset.imageAlt || '', slide };
      };
      const paint = () => { overlay.style.setProperty('--upe-x', x + '%'); overlay.style.setProperty('--upe-y', y + '%'); };
      const setState = () => {
        pieceButton.setAttribute('aria-pressed', String(!isDetail));
        detailButton.setAttribute('aria-pressed', String(isDetail));
        detailButton.setAttribute('aria-expanded', String(isDetail));
        wrap.toggleAttribute('data-piece-stage-open', isDetail);
      };
      const loadPhoto = () => {
        if (!isDetail || disposed) return;
        const photo = activePhoto();
        if (!photo.url) { closeDetail(false); return; }
        image.alt = photo.alt;
        if (photo.url === source) return;
        source = photo.url; x = 50; y = 50; paint();
        const version = ++request;
        overlay.setAttribute('data-loading', '');
        overlay.setAttribute('aria-busy', 'true');
        status.textContent = host.dataset.pieceLoading;
        image.src = photo.url;
        context.src = photo.url;
        const ready = typeof image.decode === 'function' ? image.decode() : new Promise((resolve, reject) => {
          if (image.complete) { image.naturalWidth ? resolve() : reject(); return; }
          image.addEventListener('load', resolve, { once: true, signal });
          image.addEventListener('error', reject, { once: true, signal });
        });
        ready.then(() => {
          if (version !== request || !isDetail || disposed) return;
          overlay.removeAttribute('data-loading'); overlay.removeAttribute('aria-busy'); status.textContent = '';
        }).catch(() => {
          if (version !== request || !isDetail || disposed) return;
          overlay.removeAttribute('data-loading'); overlay.removeAttribute('aria-busy'); status.textContent = host.dataset.pieceError;
          source = '';
        });
      };
      const schedulePhoto = () => {
        if (!isDetail || queuedFrame) return;
        queuedFrame = requestAnimationFrame(() => { queuedFrame = 0; loadPhoto(); });
      };
      function closeDetail(restoreFocus = false) {
        if (!isDetail) return;
        isDetail = false; ++request; source = ''; drag = null;
        viewport.removeAttribute('data-dragging');
        overlay.hidden = true;
        overlay.removeAttribute('data-loading'); overlay.removeAttribute('aria-busy'); status.textContent = '';
        stage.inert = priorInert;
        if (priorHidden === null) stage.removeAttribute('aria-hidden'); else stage.setAttribute('aria-hidden', priorHidden);
        setState();
        if (restoreFocus) detailButton.focus({ preventScroll: true });
      }
      const openDetail = () => {
        if (!activePhoto().url) return;
        if (isDetail) { viewport.focus({ preventScroll: true }); return; }
        isDetail = true;
        priorInert = stage.inert; priorHidden = stage.getAttribute('aria-hidden');
        stage.inert = true; stage.setAttribute('aria-hidden', 'true');
        overlay.hidden = false; setState(); loadPhoto();
        viewport.focus({ preventScroll: true });
      };
      const openFit = () => {
        closeDetail(false);
        const fit = product.querySelector('[data-ura-fit-integrated][data-ura-fit-inline]');
        const sheet = document.querySelector('[data-ura-mobile-nav]');
        if (mobile.matches && sheet?.dataset.mobileNavReady === 'true' && sheet.querySelector('[data-mnav-fit-view]') && fit && product.dataset.ura4Ready === 'true') {
          document.dispatchEvent(new CustomEvent('ura:mobile-panel-request', { detail: { panel: 'fit', trigger: fitButton } }));
          return;
        }
        if (fit) {
          if (fit.tagName === 'DETAILS') fit.open = true;
          fit.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' });
          fit.focus({ preventScroll: true });
        }
      };
      pieceButton.addEventListener('click', () => closeDetail(false), { signal });
      detailButton.addEventListener('click', openDetail, { signal });
      fitButton.addEventListener('click', openFit, { signal });
      overlay.querySelector('[data-piece-close]').addEventListener('click', () => closeDetail(true), { signal });
      host.addEventListener('keydown', event => {
        if (event.key === 'Escape' && isDetail) { event.preventDefault(); event.stopPropagation(); closeDetail(true); return; }
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
        const index = controls.indexOf(event.target);
        if (index < 0) return;
        event.preventDefault(); event.stopPropagation();
        const direction = getComputedStyle(host).direction === 'rtl' ? -1 : 1;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? controls.length - 1 : (index + (event.key === 'ArrowRight' ? direction : -direction) + controls.length) % controls.length;
        controls[next].focus({ preventScroll: true });
      }, { signal });
      overlay.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeDetail(true); return; }
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.stopPropagation();
        if (event.target !== viewport || event.altKey || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        if (event.key === 'Home') { x = 50; y = 50; }
        else if (event.key === 'End') { x = 100; y = 100; }
        else { x = clamp(x + (event.key === 'ArrowRight' ? 10 : event.key === 'ArrowLeft' ? -10 : 0)); y = clamp(y + (event.key === 'ArrowDown' ? 10 : event.key === 'ArrowUp' ? -10 : 0)); }
        paint();
      }, { signal });
      viewport.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        drag = { id: event.pointerId, type: event.pointerType, startX: event.clientX, startY: event.clientY, x, y, captured: false };
      }, { signal, passive: true });
      viewport.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.startX, dy = event.clientY - drag.startY;
        if (!drag.captured) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 7) return;
          if (drag.type !== 'mouse' && Math.abs(dy) >= Math.abs(dx)) { drag = null; return; }
          viewport.setPointerCapture(event.pointerId); drag.captured = true; viewport.setAttribute('data-dragging', '');
        }
        x = clamp(drag.x - dx / Math.max(1, viewport.clientWidth) * 140);
        if (drag.type === 'mouse') y = clamp(drag.y - dy / Math.max(1, viewport.clientHeight) * 140);
        paint();
      }, { signal, passive: true });
      const release = event => { if (!drag || event.pointerId !== drag.id) return; if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId); drag = null; viewport.removeAttribute('data-dragging'); };
      viewport.addEventListener('pointerup', release, { signal });
      viewport.addEventListener('pointercancel', release, { signal });
      viewport.addEventListener('dragstart', event => event.preventDefault(), { signal });
      const observer = new MutationObserver(schedulePhoto);
      observer.observe(stage, { subtree: true, attributes: true, attributeFilter: ['class', 'aria-hidden', 'src', 'srcset'] });
      product.addEventListener('change', schedulePhoto, { signal });
      window.addEventListener('pagehide', () => closeDetail(false), { signal });
      const fit = product.querySelector('[data-ura-fit-integrated][data-ura-fit-inline]');
      if (!fit) fitButton.hidden = true;
      gallery.setAttribute('data-ura-piece-ready', ''); host.hidden = false;
      instances.set(host, () => {
        disposed = true; closeDetail(false); controller.abort(); observer.disconnect(); cancelAnimationFrame(queuedFrame);
        if (overlayHome.parentNode) overlayHome.after(overlay);
        overlayHome.remove(); gallery.removeAttribute('data-ura-piece-ready'); host.hidden = true; instances.delete(host);
      });
    });
  }
  initialize(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => [...instances].forEach(([host, cleanup]) => { if (event.target.contains(host)) cleanup(); }));
})();
